/**
 * 音を鳴らす殻
 *
 * **判断はここに書かない。** 何の音をどう作るかは `music.ts` と `voice.ts` に
 * 純粋な関数で置いてあり、ここは出来上がった配列を `AudioBuffer` に載せて
 * 鳴らすだけである。`workers/room` が `@tenka/net` に対して薄い殻であるのと
 * 同じ立ち位置で、**検査できない場所（ブラウザの音の出口）に判断を置かない**。
 *
 * 【鳴らないことを異常にしない】
 * Web Audio が使えない環境、音を切っている人、まだ画面に触っていない状態。
 * どれも「鳴らない」だけで、遊びは最後まで成立する（docs/04 の
 * 「無音でも成立すること」）。この中の関数はどれも投げない。
 *
 * 【最初の指を待つ】
 * ブラウザは、人が触るまで音を出させない（自動再生の制限）。
 * 触る前に鳴らそうとすると `suspended` のまま黙って失敗するので、
 * **最初の指で開ける**。それまでに来た音は捨てる — 溜めて後で
 * まとめて鳴らすと、触った瞬間に過去の音が全部鳴る。
 */

import { renderScene, renderSfx, type SceneName, type SfxName } from './music';

/** 曲を入れ替えるときに被せる秒数。切り替えが唐突だと場面が飛んで感じる */
const FADE = 0.7;

type Ctx = AudioContext & { readonly state: AudioContextState };

export class Sound {
  private ctx: Ctx | null = null;
  private master: GainNode | null = null;
  private cache = new Map<string, AudioBuffer>();
  private playing: { name: SceneName; src: AudioBufferSourceNode; gain: GainNode } | null = null;
  private wanted: SceneName | null = null;

  private sfxOn = true;
  private musicOn = true;
  private level = 0.7;

  /** 最初の指で開ける。何度呼んでもよい */
  unlock(): void {
    try {
      if (!this.ctx) {
        const Ctor: typeof AudioContext | undefined =
          globalThis.AudioContext ??
          (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!Ctor) return;
        this.ctx = new Ctor() as Ctx;
        this.master = this.ctx.createGain();
        this.master.gain.value = this.level;
        this.master.connect(this.ctx.destination);
      }
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      // 触る前に頼まれていた曲があれば、ここで始める
      if (this.wanted && !this.playing) this.scene(this.wanted);
    } catch {
      // 音が出せないだけ。遊びは続く
    }
  }

  get ready(): boolean {
    return this.ctx !== null && this.ctx.state === 'running';
  }

  setSfx(on: boolean): void {
    this.sfxOn = on;
  }

  setMusic(on: boolean): void {
    this.musicOn = on;
    if (!on) this.stopScene();
    else if (this.wanted) this.scene(this.wanted);
  }

  setVolume(v: number): void {
    this.level = Math.min(1, Math.max(0, v));
    if (this.master && this.ctx) {
      this.master.gain.setTargetAtTime(this.level, this.ctx.currentTime, 0.05);
    }
  }

  /**
   * 波形を作って覚えておく。
   *
   * **作るのは1回だけ。** 出来上がりは端末の標本化周波数（たいてい 48kHz）
   * に合わせて作るので、作り直すと同じ音でも計算し直しになる。
   */
  private buffer(key: string, make: (sampleRate: number) => Float32Array): AudioBuffer | null {
    if (!this.ctx) return null;
    const hit = this.cache.get(key);
    if (hit) return hit;
    try {
      const data = make(this.ctx.sampleRate);
      const buf = this.ctx.createBuffer(1, data.length, this.ctx.sampleRate);
      // `copyToChannel` は共有メモリ上の配列を受け取らない型になっている。
      // ここで作った配列は共有ではないので、書き込み先へ直に写す
      buf.getChannelData(0).set(data);
      this.cache.set(key, buf);
      return buf;
    } catch {
      return null;
    }
  }

  /** 効果音を1つ鳴らす。鳴らせなければ黙って何もしない */
  play(name: SfxName): void {
    try {
      if (!this.sfxOn || !this.ctx || !this.master || this.ctx.state !== 'running') return;
      const buf = this.buffer(`sfx:${name}`, (sr) => renderSfx(name, sr));
      if (!buf) return;
      const src = this.ctx.createBufferSource();
      src.buffer = buf;
      src.connect(this.master);
      src.start();
    } catch {
      // 鳴らなかっただけ
    }
  }

  /**
   * 場面の曲に切り替える。`null` で止める。
   *
   * 同じ場面を続けて頼まれたら**何もしない** — 画面が再描画されるたびに
   * 曲が鳴り直すと、頭出しが繰り返されて落ち着かない。
   */
  scene(name: SceneName | null): void {
    try {
      this.wanted = name;
      if (!name) {
        this.stopScene();
        return;
      }
      if (!this.musicOn) return;
      if (!this.ctx || !this.master || this.ctx.state !== 'running') return;
      if (this.playing?.name === name) return;

      const buf = this.buffer(`bgm:${name}`, (sr) => renderScene(name, sr));
      if (!buf) return;

      const now = this.ctx.currentTime;
      this.fadeOut(now);

      const gain = this.ctx.createGain();
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(1, now + FADE);
      gain.connect(this.master);

      const src = this.ctx.createBufferSource();
      src.buffer = buf;
      src.loop = true;
      src.connect(gain);
      src.start();
      this.playing = { name, src, gain };
    } catch {
      // 曲が鳴らなかっただけ
    }
  }

  private fadeOut(now: number): void {
    const old = this.playing;
    if (!old) return;
    this.playing = null;
    try {
      old.gain.gain.cancelScheduledValues(now);
      old.gain.gain.setValueAtTime(old.gain.gain.value, now);
      old.gain.gain.linearRampToValueAtTime(0, now + FADE);
      // 消えきってから止める。先に止めると「ブツッ」と切れる
      old.src.stop(now + FADE + 0.05);
    } catch {
      // すでに止まっている
    }
  }

  private stopScene(): void {
    if (this.ctx) this.fadeOut(this.ctx.currentTime);
  }
}

/** 画面ぜんぶで1つ。音の出口は端末に1つしかない */
export const sound = new Sound();
